import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tag } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';
import { Lock, Archive } from 'lucide-react';

import type { Order, Address, CarrierRate } from '@/components/admin/order-detail/types';
import OrderHeader from '@/components/admin/order-detail/OrderHeader';
import OrderItemsCard from '@/components/admin/order-detail/OrderItemsCard';
import ShipmentsCard from '@/components/admin/order-detail/ShipmentsCard';
import PaymentCard from '@/components/admin/order-detail/PaymentCard';
import UnifiedTimeline from '@/components/admin/order-detail/UnifiedTimeline';
import CustomerCard from '@/components/admin/order-detail/CustomerCard';
import AddressCard from '@/components/admin/order-detail/AddressCard';
import FraudRiskCard from '@/components/admin/order-detail/FraudRiskCard';
import MetadataCard from '@/components/admin/order-detail/MetadataCard';
import MarketingCard from '@/components/admin/order-detail/MarketingCard';

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Cancelled', value: 'cancelled' },
];

const CARRIER_OPTIONS = [
  { label: 'Select carrier...', value: '' },
  { label: 'USPS', value: 'usps' },
  { label: 'UPS', value: 'ups' },
  { label: 'FedEx', value: 'fedex' },
  { label: 'DHL', value: 'dhl' },
  { label: 'Other', value: 'other' },
];

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const addToast = useUIStore((s) => s.addToast);

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Status update
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Note
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);

  // Shipment creation
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [rates, setRates] = useState<CarrierRate[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [selectedRate, setSelectedRate] = useState<CarrierRate | null>(null);
  const [shipWeight, setShipWeight] = useState('8');
  const [isCreatingShipment, setIsCreatingShipment] = useState(false);

  // Refund
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [restoreInventory, setRestoreInventory] = useState(true);
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);

  // Tags
  const [tagInput, setTagInput] = useState('');

  // Cancel
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [restockOnCancel, setRestockOnCancel] = useState(true);
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);

  // Discount
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);



  // ------ Data fetching ------

  const fetchOrder = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<Order>(`/admin/orders/${id}`);
      setOrder(response);
      setNewStatus(response.status);
      setTrackingNumber(response.tracking_number || '');
      setTrackingCarrier(response.tracking_carrier || '');
      setRefundAmount(response.total.toString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchOrder(); }, [id]);

  const refreshOrder = async () => {
    if (!id) return;
    const response = await api.get<Order>(`/admin/orders/${id}`);
    setOrder(response);
  };

  // ------ Handlers ------

  const handleUpdateStatus = async () => {
    if (!id || !order) return;
    setIsUpdatingStatus(true);
    try {
      const body: any = { status: newStatus };
      if (newStatus === 'shipped' && trackingNumber && trackingCarrier) {
        body.tracking_number = trackingNumber;
        body.tracking_carrier = trackingCarrier;
      }
      await api.put(`/admin/orders/${id}/status`, body);
      await refreshOrder();
      setShowStatusModal(false);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to update status', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !newNote.trim()) return;
    setIsAddingNote(true);
    try {
      await api.post(`/admin/orders/${id}/notes`, { note: newNote.trim() });
      await refreshOrder();
      setNewNote('');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to add note', 'error');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleRefund = async () => {
    if (!id || !refundAmount) return;
    setIsProcessingRefund(true);
    try {
      await api.post(`/admin/orders/${id}/refund`, {
        amount: parseFloat(refundAmount),
        reason: refundReason,
        restore_inventory: restoreInventory,
      });
      await refreshOrder();
      setShowRefundModal(false);
      setRefundReason('');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to process refund', 'error');
    } finally {
      setIsProcessingRefund(false);
    }
  };

  const handleFetchRates = async () => {
    if (!id) return;
    setIsLoadingRates(true);
    setShowShipmentModal(true);
    try {
      const data = await api.get<{ rates: CarrierRate[]; total_weight: number }>(`/admin/fulfillment/rates/${id}`);
      setRates(data.rates);
      setShipWeight(data.total_weight?.toString() || '8');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to fetch rates', 'error');
    } finally {
      setIsLoadingRates(false);
    }
  };

  const handleCreateShipment = async () => {
    if (!id || !selectedRate) return;
    setIsCreatingShipment(true);
    try {
      await api.post(`/admin/fulfillment/orders/${id}/shipments`, {
        carrier: selectedRate.carrier,
        service_code: selectedRate.service_code,
        service_name: selectedRate.service_name,
        weight_oz: parseFloat(shipWeight),
      });
      await refreshOrder();
      setShowShipmentModal(false);
      setSelectedRate(null);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to create shipment', 'error');
    } finally {
      setIsCreatingShipment(false);
    }
  };

  const handleDownloadLabel = async (shipmentId: string) => {
    try {
      const data = await api.get<{ url: string }>(`/admin/fulfillment/shipments/${shipmentId}/label`);
      window.open(data.url, '_blank');
    } catch {
      addToast('Failed to get label', 'error');
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setIsCancelling(true);
    try {
      await api.post(`/admin/orders/${id}/cancel`, { reason: cancelReason, restock: restockOnCancel, send_email: sendCancelEmail });
      addToast('Order cancelled', 'success');
      setShowCancelModal(false);
      await refreshOrder();
    } catch (err: any) {
      addToast(err.message || 'Failed to cancel', 'error');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!id) return;
    try {
      const res = await api.put<{ is_archived: boolean }>(`/admin/orders/${id}/archive`);
      addToast(res.is_archived ? 'Order archived' : 'Order unarchived', 'success');
      setOrder(prev => prev ? { ...prev, is_archived: res.is_archived } : prev);
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleToggleLock = async () => {
    if (!id) return;
    try {
      const res = await api.put<{ is_locked: boolean; locked_by: string | null }>(`/admin/orders/${id}/lock`);
      addToast(res.is_locked ? 'Order locked' : 'Order unlocked', 'success');
      setOrder(prev => prev ? { ...prev, is_locked: res.is_locked, locked_by: res.locked_by } : prev);
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleDuplicate = async () => {
    if (!id) return;
    try {
      const res = await api.post<{ order_id: string }>(`/admin/orders/${id}/duplicate`);
      addToast('Draft order created', 'success');
      navigate(`/admin/orders/${res.order_id}`);
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleAddTag = async (tag: string) => {
    if (!id || !order || !tag.trim()) return;
    const currentTags = (order.tags || []).map(t => t.name);
    try {
      const res = await api.put<{ tags: any[] }>(`/admin/orders/${id}/tags`, { tags: [...currentTags, tag.trim()] });
      setOrder(prev => prev ? { ...prev, tags: res.tags } : prev);
      setTagInput('');
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleRemoveTag = async (tagName: string) => {
    if (!id || !order) return;
    const newTags = (order.tags || []).filter(t => t.name !== tagName).map(t => t.name);
    try {
      const res = await api.put<{ tags: any[] }>(`/admin/orders/${id}/tags`, { tags: newTags });
      setOrder(prev => prev ? { ...prev, tags: res.tags } : prev);
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleApplyDiscount = async () => {
    if (!id || !discountAmount) return;
    setIsApplyingDiscount(true);
    try {
      const res = await api.post<Order>(`/admin/orders/${id}/discount`, { amount: parseFloat(discountAmount), reason: discountReason });
      setOrder(res);
      setShowDiscountModal(false);
      setDiscountAmount('');
      setDiscountReason('');
      addToast('Discount applied', 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleUpdateMetadata = async (key: string, value: string) => {
    if (!id) return;
    try {
      const res = await api.put<{ metadata: Record<string, any> }>(`/admin/orders/${id}/metadata`, { metadata: { [key]: value } });
      setOrder(prev => prev ? { ...prev, metadata: res.metadata } : prev);
      addToast('Metadata updated', 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleDeleteMetadata = async (key: string) => {
    if (!id || !order) return;
    const newMeta = { ...(order.metadata || {}) };
    delete newMeta[key];
    try {
      await api.put(`/admin/orders/${id}/metadata`, { metadata: { [key]: null } });
      setOrder(prev => prev ? { ...prev, metadata: newMeta } : prev);
    } catch (err: any) {
      addToast(err.message || 'Failed', 'error');
    }
  };

  const handleResendEmail = async (type: 'confirmation' | 'shipping' | 'review') => {
    if (!id) return;
    try {
      const endpoint = type === 'confirmation' ? 'resend-confirmation' : type === 'shipping' ? 'resend-shipping' : 'resend-review';
      await api.post(`/admin/orders/${id}/${endpoint}`);
      addToast(`${type.charAt(0).toUpperCase() + type.slice(1)} email sent`, 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to send email', 'error');
    }
  };

  const handleSendInvoice = async () => {
    if (!id) return;
    try {
      await api.post(`/admin/orders/${id}/send-invoice`);
      addToast('Invoice sent to customer', 'success');
      await refreshOrder();
    } catch (err: any) {
      addToast(err.message || 'Failed to send invoice', 'error');
    }
  };

  const handleSaveShippingAddress = async (address: Address) => {
    if (!id) return;
    await api.put(`/admin/orders/${id}/shipping-address`, address);
    setOrder(prev => prev ? { ...prev, shipping_address: address } : prev);
    addToast('Shipping address updated', 'success');
  };

  const handleSaveBillingAddress = async (address: Address) => {
    if (!id) return;
    await api.put(`/admin/orders/${id}/billing-address`, address);
    setOrder(prev => prev ? { ...prev, billing_address: address } : prev);
    addToast('Billing address updated', 'success');
  };

  // ------ Render ------

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading order details...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/orders')}>
          Back to Orders
        </Button>
        <Card padding="lg">
          <div className="text-center text-red-600" role="alert">
            {error || 'Order not found'}
          </div>
        </Card>
      </div>
    );
  }

  const showTrackingFields = newStatus === 'shipped';

  return (
    <div className="space-y-6">
      {/* Header */}
      <OrderHeader
        order={order}
        onCancel={() => setShowCancelModal(true)}
        onArchive={handleToggleArchive}
        onLock={handleToggleLock}
        onDuplicate={handleDuplicate}
        onBack={() => navigate('/admin/orders')}
      />

      {/* Tags row */}
      <div className="flex flex-wrap items-center gap-2">
        {(order.tags || []).map(tag => (
          <span key={tag.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {tag.name}
            <button onClick={() => handleRemoveTag(tag.name)} className="hover:text-blue-600">&times;</button>
          </span>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); handleAddTag(tagInput); }} className="inline-flex items-center">
          <input
            type="text" value={tagInput} onChange={e => setTagInput(e.target.value)}
            placeholder="Add tag..." className="text-xs border border-gray-300 rounded-full px-2.5 py-1 w-24 focus:w-36 transition-all focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </form>
        {order.is_locked && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <Lock className="h-3 w-3" /> Locked
          </span>
        )}
        {order.is_archived && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            <Archive className="h-3 w-3" /> Archived
          </span>
        )}
      </div>

      {/* Grid: Main + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          <OrderItemsCard
            items={order.items}
            orderStatus={order.status}
            onEditItems={() => setShowStatusModal(true)}
            onFulfillItems={handleFetchRates}
          />

          <ShipmentsCard
            shipments={order.shipments || []}
            trackingNumber={order.tracking_number}
            trackingCarrier={order.tracking_carrier}
            onDownloadLabel={handleDownloadLabel}
          />

          <PaymentCard
            order={order}
            onApplyDiscount={() => setShowDiscountModal(true)}
            onRefund={() => setShowRefundModal(true)}
            onSendInvoice={handleSendInvoice}
            onResendEmail={handleResendEmail}
          />

          <UnifiedTimeline
            statusHistory={order.status_history}
            fulfillmentEvents={order.fulfillment_events || []}
            newNote={newNote}
            isAddingNote={isAddingNote}
            onNoteChange={setNewNote}
            onAddNote={handleAddNote}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <CustomerCard
            customer={order.customer}
            email={order.email}
          />

          {order.shipping_address && (
            <AddressCard
              title="Shipping Address"
              address={order.shipping_address}
              onSave={handleSaveShippingAddress}
            />
          )}

          {order.billing_address && (
            <AddressCard
              title="Billing Address"
              address={order.billing_address}
              onSave={handleSaveBillingAddress}
            />
          )}

          <FraudRiskCard
            ipAddress={order.ip_address}
            userAgent={order.user_agent}
            shippingAddress={order.shipping_address}
            billingAddress={order.billing_address}
          />

          <MetadataCard
            metadata={order.metadata || {}}
            onUpdate={handleUpdateMetadata}
            onDelete={handleDeleteMetadata}
          />

          <MarketingCard utmData={order.utm_data} />
        </div>
      </div>

      {/* ---- Modals ---- */}

      {/* Status Update Modal */}
      <Modal isOpen={showStatusModal} onClose={() => setShowStatusModal(false)} title="Update Order Status">
        <div className="space-y-4">
          <Select label="New Status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} options={STATUS_OPTIONS} />
          {showTrackingFields && (
            <>
              <Select label="Tracking Carrier" value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)} options={CARRIER_OPTIONS} placeholder="Select carrier..." />
              <Input label="Tracking Number" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Enter tracking number" />
            </>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleUpdateStatus} isLoading={isUpdatingStatus}
              disabled={!newStatus || (showTrackingFields && (!trackingNumber || !trackingCarrier))}>
              Update Status
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Shipment Modal */}
      <Modal isOpen={showShipmentModal} onClose={() => { setShowShipmentModal(false); setSelectedRate(null); }} title="Create Shipment">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Package Weight (oz)</label>
            <input type="number" value={shipWeight} onChange={(e) => setShipWeight(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </div>
          {isLoadingRates ? (
            <div className="flex justify-center py-8"><LoadingSpinner /></div>
          ) : rates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No rates available. Check shipping configuration.</p>
          ) : (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Select Service</h4>
              {rates.map((rate) => (
                <button key={rate.service_code} onClick={() => setSelectedRate(rate)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedRate?.service_code === rate.service_code ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{rate.service_name}</span>
                      <span className="text-xs text-gray-500 ml-2 uppercase">{rate.carrier === 'flat' ? 'Flat Rate' : rate.carrier}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-gray-900">{formatCurrency(rate.price)}</span>
                      {rate.estimated_days_min && (
                        <p className="text-xs text-gray-500">{rate.estimated_days_min}{rate.estimated_days_max ? `-${rate.estimated_days_max}` : ''} days</p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => { setShowShipmentModal(false); setSelectedRate(null); }}>Cancel</Button>
            <Button variant="primary" onClick={handleCreateShipment} isLoading={isCreatingShipment} disabled={!selectedRate}>
              <Tag className="h-4 w-4 mr-1" />{selectedRate?.carrier === 'flat' ? 'Create Shipment' : 'Purchase Label'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Refund Modal */}
      <Modal isOpen={showRefundModal} onClose={() => setShowRefundModal(false)} title="Issue Refund">
        <div className="space-y-4">
          <Input label="Refund Amount" type="number" step="0.01" value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)} helperText={`Maximum: ${formatCurrency(order.total)}`} />
          <Textarea label="Reason (optional)" value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Reason for refund..." rows={3} />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="restore-inventory" checked={restoreInventory} onChange={(e) => setRestoreInventory(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <label htmlFor="restore-inventory" className="text-sm text-gray-700">Restore inventory quantities</label>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowRefundModal(false)}>Cancel</Button>
            <Button variant="danger" onClick={handleRefund} isLoading={isProcessingRefund}
              disabled={!refundAmount || parseFloat(refundAmount) <= 0}>
              Issue Refund
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Order Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Order">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Cancel order <strong>#{order.order_number}</strong>?</p>
          <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancellation (optional)" rows={2} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={restockOnCancel} onChange={e => setRestockOnCancel(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Restore inventory
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sendCancelEmail} onChange={e => setSendCancelEmail(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            Send cancellation email to customer
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowCancelModal(false)}>Keep Order</Button>
            <Button variant="danger" onClick={handleCancel} isLoading={isCancelling}>Cancel Order</Button>
          </div>
        </div>
      </Modal>

      {/* Apply Discount Modal */}
      <Modal isOpen={showDiscountModal} onClose={() => setShowDiscountModal(false)} title="Apply Discount">
        <div className="space-y-4">
          <Input label="Discount Amount ($)" type="number" step="0.01" min="0.01" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} required />
          <Textarea label="Reason (optional)" value={discountReason} onChange={e => setDiscountReason(e.target.value)} rows={2} />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowDiscountModal(false)}>Cancel</Button>
            <Button onClick={handleApplyDiscount} isLoading={isApplyingDiscount}>Apply Discount</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
